import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Message, Research, ResearchId } from "../AppState.ts";
import { DeepResearchService } from "../DeepResearchService.ts";
import { useDeepResearchService } from "./useDeepResearchService.tsx";

const emptyArray: Message[] = [];
const emptyObject: Record<string, never> = {};

export const useAppState = <T>(
  deepResearchService: DeepResearchService,
  selector: () => T,
): T => useSyncExternalStore(deepResearchService.subscribe, selector);

export const useAuthState = () => {
  const appService = useDeepResearchService();
  return useAppState(appService, () => appService.getState().authState);
};

export const useResearchesState = () => {
  const appService = useDeepResearchService();
  return useAppState(appService, () => appService.getState());
};

export const useResearchState = (researchId: ResearchId): Research | null => {
  const appService = useDeepResearchService();
  return useAppState(
    appService,
    () => appService.getState().researchMap.items[researchId] || null,
  );
};

export const useMessagesState = (researchId: ResearchId): Message[] => {
  const appService = useDeepResearchService();
  const messages = useAppState(appService, () => {
    const messages = appService.getState().messages.items[researchId];
    return messages || emptyArray;
  });
  return messages;
};

export const useReferencesState = (researchId: ResearchId) => {
  const appService = useDeepResearchService();
  return useAppState(
    appService,
    () => appService.getState().fileReferences.items[researchId] || emptyArray,
  );
};

export const useFiltersState = (researchId: ResearchId) => {
  const appService = useDeepResearchService();
  return useAppState(
    appService,
    () => appService.getState().filters.items[researchId] || emptyObject,
  );
};

export const useFiltersLoadingState = () => {
  const appService = useDeepResearchService();
  return useAppState(appService, () => appService.getState().filters.loading);
};

export const useFilterOptionsState = () => {
  const appService = useDeepResearchService();
  return useAppState(
    appService,
    () => appService.getState().filterOptions.items || emptyObject,
  );
};

export const useFullFilterOptionsState = () => {
  const appService = useDeepResearchService();
  return useAppState(appService, () => appService.getState().filterOptions);
};

export const useKnowledgeSources = () => {
  const appService = useDeepResearchService();
  return useAppState(
    appService,
    () => appService.getState().knowledgeSources.items,
  );
};

export const useKnowledgeSourcesLoading = () => {
  const appService = useDeepResearchService();
  return useAppState(
    appService,
    () => appService.getState().knowledgeSources.loading,
  );
};

export const useResearchTypes = () => {
  const appService = useDeepResearchService();
  return useAppState(appService, () => appService.getState().researchTypes);
};

export const useTermsAndConditionsState = () => {
  const appService = useDeepResearchService();
  return useAppState(
    appService,
    () => appService.getState().termsAndConditions,
  );
};

export const useResearchHistory = () => {
  const appService = useDeepResearchService();

  useEffect(() => {
    appService.refreshResearches();
  }, [appService]);

  return useAppState(appService, () => appService.getState().researchMap);
};

export const useResearchesLoading = () => {
  const appService = useDeepResearchService();
  return useAppState(
    appService,
    () => appService.getState().researchMap.loading,
  );
};

export const useIsMessageBoxSending = () => {
  const appService = useDeepResearchService();
  return useAppState(appService, () => appService.getState().messageBoxSending);
};

export const useIsResearchSending = (researchId: ResearchId): boolean => {
  const messages = useMessagesState(researchId);
  return messages.some(
    (msg) => msg.status === "optimistic" || msg.status === "processing",
  );
};

export const useResearchMessages = (
  researchId: ResearchId,
): {
  messages: ReturnType<typeof useMessagesState>;
  loading: boolean;
} => {
  const appService = useDeepResearchService();
  const isResearchSending = useIsResearchSending(researchId);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isResearchSending) return;
    setLoading(true);
    appService
      .refreshResearchItem({ id: researchId })
      .then(() =>
        appService.refreshMessages({
          researchId,
          useInitialQueryFallback: true,
        }),
      )
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, [appService, researchId, isResearchSending]);

  const messages = useMessagesState(researchId);

  return useMemo(
    () => ({
      messages,
      loading,
    }),
    [messages, loading],
  );
};
